<template>
  <div class="modal-mask">
    <div class="modal-wrapper">
      <div class="modal-container settings-container">
        <div class="modal-header d-flex justify-content-between align-items-start">
          <div>
            <h3 class="mb-1">Einstellungen</h3>
            <p class="text-muted mb-0 small" v-if="currentUser && currentUser.name">
              Angemeldet als <strong>{{ currentUser.name }}</strong>
              <span v-if="currentUser.email"> ({{ currentUser.email }})</span>
            </p>
          </div>
          <button type="button" class="close" @click="$emit('close')" aria-label="Schließen">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>

        <div class="modal-body">
          <div v-if="error" class="alert alert-danger" role="alert">{{ error }}</div>
          <div v-if="info" class="alert alert-success" role="alert">{{ info }}</div>

          <div class="mb-4">
            <button class="btn btn-outline-secondary" @click="doLogout" :disabled="busy">Abmelden</button>
          </div>

          <template v-if="currentUser && currentUser.isadmin">
            <hr />
            <h5 class="mb-3">Benutzerverwaltung</h5>

            <div class="table-responsive mb-4">
              <table class="table table-sm table-hover mb-0">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>E-Mail</th>
                    <th class="text-right">Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="u in users" :key="u.id">
                    <td>
                      {{ u.name }}
                      <span v-if="u.isadmin" class="badge badge-dark ml-1">Config-Admin</span>
                    </td>
                    <td>{{ u.email }}</td>
                    <td class="text-right text-nowrap">
                      <button
                        type="button"
                        class="btn btn-link btn-sm p-1"
                        :disabled="u.isadmin || busy"
                        title="Passwort zurücksetzen"
                        @click="startResetPassword(u)"
                      >
                        <i class="fas fa-key"></i>
                      </button>
                      <button
                        type="button"
                        class="btn btn-link btn-sm p-1 text-danger"
                        :disabled="u.isadmin || busy"
                        title="Löschen"
                        @click="deleteUser(u)"
                      >
                        <i class="fas fa-trash-alt"></i>
                      </button>
                    </td>
                  </tr>
                  <tr v-if="!users.length">
                    <td colspan="3" class="text-muted">Keine Benutzer</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div v-if="resetUser" class="card mb-4">
              <div class="card-body">
                <h6 class="card-title">Neues Passwort für {{ resetUser.name }}</h6>
                <input
                  class="form-control mb-2"
                  type="password"
                  v-model="resetPassword"
                  placeholder="Mindestens 8 Zeichen"
                  :disabled="busy"
                  @keyup.enter="savePassword"
                />
                <div class="d-flex justify-content-end">
                  <button class="btn btn-outline-secondary btn-sm mr-2" @click="cancelReset" :disabled="busy">Abbrechen</button>
                  <button class="btn btn-primary btn-sm" @click="savePassword" :disabled="busy || resetPassword.length < 8">
                    Speichern
                  </button>
                </div>
              </div>
            </div>

            <div class="card">
              <div class="card-body">
                <h6 class="card-title">Benutzer anlegen</h6>
                <div class="form-group">
                  <label for="new-name">Name</label>
                  <input id="new-name" class="form-control" type="text" v-model="newName" :disabled="busy" />
                </div>
                <div class="form-group">
                  <label for="new-email">E-Mail</label>
                  <input id="new-email" class="form-control" type="email" v-model="newEmail" :disabled="busy" />
                </div>
                <div class="form-group">
                  <label for="new-password">Passwort</label>
                  <input id="new-password" class="form-control" type="password" v-model="newPassword" :disabled="busy" />
                </div>
                <button
                  class="btn btn-success"
                  @click="createUser"
                  :disabled="busy || !newName || !newEmail || newPassword.length < 8"
                >
                  Anlegen
                </button>
              </div>
            </div>
          </template>

          <template v-else>
            <hr />
            <p class="text-muted mb-0">Benutzerverwaltung nur für den Administrator.</p>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'SettingsModal',
  props: {
    currentUser: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      users: [],
      error: '',
      info: '',
      busy: false,
      newName: '',
      newEmail: '',
      newPassword: '',
      resetUser: null,
      resetPassword: '',
    };
  },
  mounted() {
    if (this.currentUser && this.currentUser.isadmin) {
      this.loadUsers();
    }
  },
  methods: {
    clearMessages() {
      this.error = '';
      this.info = '';
    },
    handleAuthError(error) {
      if (!error.response || error.response.status === 401) {
        this.$emit('logout');
        return true;
      }
      return false;
    },
    loadUsers() {
      this.clearMessages();
      axios.get(process.env.VUE_APP_BACKEND + '/api/users')
        .then((response) => {
          this.users = response.data || [];
        })
        .catch((error) => {
          if (this.handleAuthError(error)) return;
          this.error = (error.response && error.response.data && error.response.data.message)
            || 'Benutzerliste konnte nicht geladen werden';
        });
    },
    createUser() {
      this.clearMessages();
      this.busy = true;
      axios.post(process.env.VUE_APP_BACKEND + '/api/users', {
        name: this.newName,
        email: this.newEmail,
        password: this.newPassword,
      }, {
        headers: { 'Content-Type': 'application/json' },
      }).then(() => {
        this.info = 'Benutzer angelegt';
        this.newName = '';
        this.newEmail = '';
        this.newPassword = '';
        this.loadUsers();
      }).catch((error) => {
        if (this.handleAuthError(error)) return;
        this.error = (error.response && error.response.data && error.response.data.message)
          || 'Benutzer konnte nicht angelegt werden';
      }).finally(() => {
        this.busy = false;
      });
    },
    deleteUser(user) {
      if (!window.confirm('Benutzer „' + user.name + '“ wirklich löschen?')) {
        return;
      }
      this.clearMessages();
      this.busy = true;
      axios.delete(process.env.VUE_APP_BACKEND + '/api/users/' + user.id)
        .then(() => {
          this.info = 'Benutzer gelöscht';
          if (this.resetUser && this.resetUser.id === user.id) {
            this.cancelReset();
          }
          this.loadUsers();
        })
        .catch((error) => {
          if (this.handleAuthError(error)) return;
          this.error = (error.response && error.response.data && error.response.data.message)
            || 'Benutzer konnte nicht gelöscht werden';
        })
        .finally(() => {
          this.busy = false;
        });
    },
    startResetPassword(user) {
      this.clearMessages();
      this.resetUser = user;
      this.resetPassword = '';
    },
    cancelReset() {
      this.resetUser = null;
      this.resetPassword = '';
    },
    savePassword() {
      if (!this.resetUser) return;
      this.clearMessages();
      this.busy = true;
      axios.put(process.env.VUE_APP_BACKEND + '/api/users/' + this.resetUser.id + '/password', {
        password: this.resetPassword,
      }, {
        headers: { 'Content-Type': 'application/json' },
      }).then(() => {
        this.info = 'Passwort aktualisiert';
        this.cancelReset();
      }).catch((error) => {
        if (this.handleAuthError(error)) return;
        this.error = (error.response && error.response.data && error.response.data.message)
          || 'Passwort konnte nicht gesetzt werden';
      }).finally(() => {
        this.busy = false;
      });
    },
    doLogout() {
      this.$emit('logout');
    },
  },
};
</script>

<style lang="scss" scoped>
.modal-mask {
  position: fixed;
  z-index: 9998;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.75);
  display: table;
}

.modal-wrapper {
  display: table-cell;
  vertical-align: middle;
}

.modal-container {
  width: 640px;
  max-width: 95vw;
  max-height: 90vh;
  overflow-y: auto;
  margin: 0px auto;
  padding: 20px 30px;
  background-color: #fff;
  border-radius: 2px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.33);
  font-family: Helvetica, Arial, sans-serif;
}

.modal-body {
  margin: 20px 0 0;
}
</style>
